import { AppButton } from 'components';
import Editor from 'components/CKEditor/Editor';
import DOMPurify from 'dompurify';
import useManageTopicGeek from 'hooks/API/useManageTopicGeek';
import useToaster from 'hooks/useToaster';
import Joi from 'joi';
import { Fragment, useContext, useEffect, useState } from 'react';
import { AuthContext } from 'context/AuthContext';
import { RiDeleteBinLine } from 'react-icons/ri';
import { Link, useParams } from 'react-router-dom';
import { getRandomUid, isValidUuid } from 'utilities/uuid.service';
import { blockMasterSchema, TopicAudioTag, TopicVideoTag } from '../Notes';
import AppBlocks from '../Notes/Blocks/AppBlocks';
import UploadBackDrop from '../Notes/Blocks/UploadBackDrop';
import EquationBox from '../Notes/Blocks/EquationBox';
import ReviewContent from '../ReviewContent';
import { EditorPreviewContext } from 'context/EditorPreviewContext';
import { BsPencil } from 'react-icons/bs';
import { FcOk } from 'react-icons/fc';
import { IoIosEye } from 'react-icons/io';
import * as AppPics from 'assets/images';
import { ROLES } from 'utilities/constants';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { MdOutlinePreview } from 'react-icons/md';
import { IconButton, Tooltip } from '@mui/material';
import useDebounce from 'hooks/useDebounce';
import SetTime from '../SetTime';
import UserSelect from 'components/molecules/UserSelect';
import TutorRating from 'components/TutorRating';
import RearrangeBlocks from '../RearrangeBlocks';
import RatingUpdateModal from 'pages/manage-quiz/manage/Questions/RatingUpdateModal';

const RATE_UPDATE_CONFIG = {
  show: false,
  data: {},
};

function TopicGeekCornerTab({ data, subtopicID, userContext }) {
  const { PreviewBtn } = useContext(EditorPreviewContext);
  const { classID, courseID, chapterID, topicID } = useParams();
  const { userPermissions = {}, userInfo = {} } = userContext ?? {};
  const { authState } = useContext(AuthContext);
  const token = authState?.token;
  const [openUploadModal, toggleUploadModal] = useState({ currentBlock: '', block: null });
  const [reOrderBlocks, setReOrderBlocks] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [geek, setGeek] = useState([]);
  const [status, setStatus] = useState('inactive');
  const [setTime, handleSetTime] = useState({ timeLabel: 'Min', time: 0 });

  const [updatingRatingModal, setUpdatingRatingModal] = useState(
    Object.assign({}, RATE_UPDATE_CONFIG)
  );

  const [contentOwner, setContentOwner] = useState(null);
  const [contentTutor, setContentTutor] = useState(null);
  const [rating, setRating] = useState(null);

  const isTeacher = userInfo?.data?.key === ROLES.TUTOR;
  const isSE = userInfo?.data?.key === ROLES.SUBJECT_EXPERT;
  const isAdmin = userInfo?.data?.key === ROLES.ADMIN;

  const canSave = (bypass) => {
    if (isTeacher && !bypass) return false;
    return typeof geek[0]?.canSave === 'boolean' ? geek[0]?.canSave : true;
  };

  const [formErrors, setFormErrors] = useState([]);

  useEffect(() => {
    if (data?.geekcorner) setGeek(data.geekcorner);

    return () => {};
  }, [data?.geekcorner]);

  useEffect(() => {
    if (geek[0]?.blocks) {
      const __blocks = geek[0]?.blocks
        ?.map((x, i) => ({
          ...x,
          id: x._id,
          sortPosition: x.sortPosition ?? i,
        }))
        .sort((a, b) => a.sortPosition > b.sortPosition);
      setBlocks(__blocks);
      setReOrderBlocks(__blocks);
    }
    if (geek[0]?.status) setStatus(geek[0]?.status);
    if (geek[0]?.readTime)
      handleSetTime((x) => ({ ...geek[0]?.readTime, ...(geek[0]?.readTime || {}) }));
    if (geek[0]?.contentOwner) setContentOwner(geek[0]?.contentOwner);
    if (geek[0]?.assignedTutorForReview) setContentTutor(geek[0]?.assignedTutorForReview);

    if (geek[0]?.ratings?.currentRating) setRating(geek[0]?.ratings?.currentRating);
  }, [geek]);

  const { mutate, isLoading, isError } = useManageTopicGeek({
    topicID,
    axiosConfigs: {
      params: { subtopicID },
    },
  });
  useToaster(isLoading, { type: isError ? 'error' : 'success' });

  function handleSave(args = {}, latest = {}) {
    if (isLoading) return;

    const geekID = geek[0]?._id || geek[0]?.id;
    const plans = geek[0]?.plans;

    const data = {
      status,
      ...args,
      title: 'Geek corner',
      geekID,
      id: geekID,
      _id: geekID,
      plans,
      blocks,
      contentOwner: contentOwner?._id,
      assignedTutorForReview: contentTutor?._id,
      ...latest,
    };

    const validation = manageGeekSchema.validate(data, {
      abortEarly: false,
      allowUnknown: true,
    });

    if (validation?.error) {
      setFormErrors(validation.error?.details ?? []);
      return;
    }

    const apiData = {
      geekcorner: [data],
      setTime,
      subtopicID,
      type: geekID ? 'UPDATE' : 'CREATE',
      reOrderingQuotient: latest?.reOrderingQuotient,
    };

    mutate(apiData, {
      onSuccess() {
        latest?.cb?.();
      },
    });
  }

  function BlockEditBtn({ block, enableEdit }) {
    return (
      <AppButton
        variantClass="btn btn-md btn-danger mx-4"
        onClick={() => enableEdit(block.id, block.editBlock)}
      >
        {!block.editBlock && (
          <>
            <BsPencil />
            Edit Block
          </>
        )}
        {block.editBlock && (
          <>
            <FcOk />
            Done Editing
          </>
        )}
      </AppButton>
    );
  }

  function BlockDeleteBtn({ block }) {
    return (
      <AppButton
        variantClass="btn btn-md btn-danger mx-4"
        onClick={() => setBlocks(blocks.filter((x) => x.id !== block.id))}
      >
        <RiDeleteBinLine />
        Delete
      </AppButton>
    );
  }

  function enableEditing(id, currentStatus) {
    let currentBlocks = blocks;
    currentBlocks = currentBlocks.map((m) => {
      id === m.id ? (m.editBlock = !currentStatus) : (m.editBlock = false);
      return m;
    });
    setBlocks(currentBlocks);
    if (currentStatus) updateCurrentEditBlock({});
  }

  function updateBlock(obj = {}, block) {
    // If no type is selected; then do nothing.
    if (!obj.currentBlock) return;

    setBlocks((b) =>
      b.map((x) => {
        if (x.id === block.id) {
          return { ...x, ...obj };
        }
        return x;
      })
    );
  }

  const __linkParams = { lessonType: 'geek_corner' };
  if (subtopicID) __linkParams.subtopic = subtopicID;

  const commentsAvailable = () => {
    const { data = { key: '' } } = userInfo;
    if (
      `${data?.key}`.toLowerCase() === 'SUBJECT_EXPERT'.toLowerCase() &&
      geek[0]?.status === 'review'
    ) {
      return true;
    }
    if (
      `${data?.key}`.toLowerCase() === 'TUTOR'.toLowerCase() &&
      geek[0].status === 'teacher_review'
    ) {
      return true;
    }
    if (`${data?.key}`.toLowerCase() === 'QC'.toLowerCase()) {
      return true;
    }
    if (`${data?.key}`.toLowerCase() === 'ADMIN'.toLowerCase()) {
      return true;
    }
    return false;
  };

  const respondentHasComments = () => geek[0]?.viewCommentButton;

  const blockTypeExists = () =>
    geek[0]?.blocks?.some(
      (block) => !['video', 'image', 'file', 'audio'].includes(block.currentBlock)
    );

  const canMarkComments = blockTypeExists() && commentsAvailable() && respondentHasComments();

  const canChangeStatus = () =>
    typeof geek[0]?.changeStatus === 'boolean' ? geek[0]?.changeStatus : true;
  const [currentEdited, updateCurrentEditBlock] = useState({});

  const editorData = useDebounce(currentEdited, 100);

  useEffect(() => {
    if (editorData.id) {
      updateCurrentEditBlock(editorData);
      setBlocks(
        blocks.map((x) => {
          if (x.id === editorData.id) return { ...x, value: editorData.data };
          return x;
        })
      );
    }
  }, [editorData]);

  const teacherCannotChangeStatus =
    isTeacher && !rating && canChangeStatus() && status === 'teacher_review';

  return (
    <>
      {updatingRatingModal.show && (
        <RatingUpdateModal
          show={updatingRatingModal.show}
          closeModal={() => {
            setUpdatingRatingModal(Object.assign({}, RATE_UPDATE_CONFIG));
          }}
          data={updatingRatingModal.data}
        />
      )}
      <div className="container ninty_percent_width p-0 relative">
        {openUploadModal.currentBlock && (
          <UploadBackDrop
            data={{ openUploadModal }}
            closeModal={() => {
              toggleUploadModal({ currentBlock: '', block: null });
            }}
            handleSave={(obj, block) => {
              if (block) {
                updateBlock(obj, block);
                toggleUploadModal({ currentBlock: '', block: null });
                return;
              }
              setBlocks(
                blocks.concat({
                  ...blockMasterSchema,
                  id: getRandomUid(),
                  ...obj,
                })
              );
              toggleUploadModal({ currentBlock: '', block: null });
            }}
          />
        )}
        <div className="row">
          <div className="col-12 publishheader">
            <h2 className="prime-head p-0 boldtitle">Geek corner (Chapter Based)</h2>
            <div className="row  mb-4 btnsubmitmain">
              <div className="btnTab-foot">
                {userPermissions?.topic?.geekCorner?.canPublish && (
                  <button
                    type="button"
                    className="btn-right-content next"
                    onClick={() => {
                      setStatus('active');
                      handleSave({ status: 'active', isPublished: !geek[0]?.isPublished ?? false });
                    }}
                  >
                    {!geek[0]?.isPublished ? 'Publish Contents' : 'Unpublish'}
                  </button>
                )}
              </div>
              &nbsp;&nbsp;
              {canMarkComments &&
                (userPermissions?.topic?.geekCorner?.canAddComments ||
                  userPermissions?.topic?.geekCorner?.canViewComments) && (
                  <>
                    {process.env.REACT_APP_WETTING_VERSION === 'v2' ? (
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-right-content next"
                        href={`${
                          process.env.REACT_APP_ADD_VIEWCOMMENTS_BASE_URL
                        }?class=${classID}&course=${courseID}&chapter=${chapterID}&topic=${topicID}&token=${token}&${new URLSearchParams(
                          __linkParams
                        ).toString()}`}
                      >
                        {`${userInfo?.data?.key}`.toLowerCase() === 'SUBJECT_EXPERT'.toLowerCase()
                          ? 'Respond/View comments'
                          : 'Add/View comments'}
                      </a>
                    ) : (
                      <Link
                        target={'_blank'}
                        to={`/classes/${classID}/courses/${courseID}/chapters/${chapterID}/topics/${topicID}/contentPreview?${new URLSearchParams(
                          __linkParams
                        ).toString()}`}
                        className="btn-right-content next"
                      >
                        {`${userInfo?.data?.key}`.toLowerCase() === 'SUBJECT_EXPERT'.toLowerCase()
                          ? `Respond/View comments`
                          : `Add/View comments`}
                      </Link>
                    )}
                  </>
                )}
              {/* Add comment history button (like note) */}
              <Link
                to={`/classes/${classID}/courses/${courseID}/chapters/${chapterID}/topics/${topicID}/commentHistory`}
                target={'_blank'}
              >
                <Tooltip title="Comments">
                  <IconButton color="primary" aria-label="Comments section">
                    <AiOutlineInfoCircle />
                  </IconButton>
                </Tooltip>
              </Link>
              <a
                href={`${process.env.REACT_APP_PREVIEW_URL.replace(/\/$/, '')}/sneak-peak?view=geek&course=${courseID}&chapter=${chapterID}&lesson=${topicID}&subtopic=${subtopicID}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-plane g--tint ml-4 d-flex align-items-center"
              >
                {' '}
                Live Preview <MdOutlinePreview className="mx-2" size={24} />
              </a>
            </div>
            {userPermissions?.topic?.geekCorner?.showDraft && (
              <div className="custom-control custom-checkbox draftcheckbox">
                <input
                  type="checkbox"
                  name="geeksStatus"
                  className="custom-control-input"
                  id="geeksStatus"
                  checked={status === 'draft'}
                  onChange={() => setStatus(status === 'draft' ? 'inactive' : 'draft')}
                />
                <label className="custom-control-label" htmlFor="geeksStatus">
                  Draft
                </label>
              </div>
            )}
          </div>
        </div>

        <SetTime
          handleTime={(data = {}) => handleSetTime((x) => ({ ...x, ...data }))}
          {...setTime}
        />

        <div className="row Texteditorsection relative">
          {blocks.map((block) => {
            return (
              <Fragment key={block.id}>
                {['text', 'problem', 'tip', 'box'].includes(block.currentBlock) && (
                  <div className="col-12 block__mar_arrng">
                    <div className="relative">
                      <Editor
                        data={{
                          value: DOMPurify.sanitize(block.value || ''),
                          onChange: (data) => {
                            updateCurrentEditBlock({ id: block.id, data });
                            // setBlocks(
                            //   blocks.map((x) => {
                            //     if (x.id === block.id) return { ...x, value: data };
                            //     return x;
                            //   })
                            // );
                          },
                        }}
                      />
                      {!block.editBlock && (
                        <>
                          <div className="____edit___blocks"></div>
                        </>
                      )}
                    </div>
                    <div className="___icon_label_button">
                      {block.editBlock && !['text'].includes(block.currentBlock) && (
                        <>
                          <input
                            placeholder="Add caption here..."
                            style={{ width: '50%' }}
                            value={block.caption || ''}
                            onChange={(event) => {
                              const caption = event.target.value;
                              setBlocks(
                                blocks.map((x) => {
                                  if (x.id === block.id) {
                                    x.caption = caption;
                                  }

                                  return x;
                                })
                              );
                            }}
                          />
                        </>
                      )}
                      {block.editBlock && ['problem'].includes(block.currentBlock) && (
                        <>
                          <input
                            placeholder="Add exam name..."
                            className="mx-2"
                            style={{ width: '30%' }}
                            value={block.problemFrom || ''}
                            onChange={(event) => {
                              const problemFrom = event.target.value;
                              setBlocks(
                                blocks.map((x) => {
                                  if (x.id === block.id) {
                                    x.problemFrom = problemFrom;
                                  }

                                  return x;
                                })
                              );
                            }}
                          />
                          <input
                            placeholder="Add exam year..."
                            className="mx-2"
                            style={{ width: '30%' }}
                            value={block.problemYear || ''}
                            onChange={(event) => {
                              const problemYear = event.target.value;
                              setBlocks(
                                blocks.map((x) => {
                                  if (x.id === block.id) {
                                    x.problemYear = problemYear;
                                  }

                                  return x;
                                })
                              );
                            }}
                          />
                        </>
                      )}
                      {canSave() && <BlockEditBtn block={block} enableEdit={enableEditing} />}
                      {canSave() && <BlockDeleteBtn block={block} />}
                      <PreviewBtn label={'Preview Block'} labelIcon={<IoIosEye />} block={block} />
                      {/* <AddBlock block={block} /> */}
                    </div>
                  </div>
                )}
                {block.currentBlock === 'image' && (
                  <div className="__image_tool ___blockTools blue__light__border flex-p flex-column jc-center ai-center">
                    <div className="ratio ratio-21x9">
                      <img src={block.assetUrl} alt="presentational block content" />
                    </div>
                    <div className="___icon_label_button">
                      <>
                        <input
                          placeholder="Add caption here..."
                          style={{ width: '50%' }}
                          value={block.caption || ''}
                          onChange={(event) => {
                            const caption = event.target.value;
                            setBlocks(
                              blocks.map((x) => {
                                if (x.id === block.id) {
                                  x.caption = caption;
                                }

                                return x;
                              })
                            );
                          }}
                        />
                      </>
                      {canSave() && <BlockDeleteBtn block={block} />}
                      {canSave() && (
                        <AppButton
                          variantClass="btn btn-md btn-danger mx-4"
                          onClick={() => {
                            toggleUploadModal({ currentBlock: 'image', block });
                          }}
                        >
                          <BsPencil />
                          Edit Block
                        </AppButton>
                      )}
                    </div>
                  </div>
                )}
                {block.currentBlock === 'video' && (
                  <div className="__video_tool ___blockTools blue__light__border flex-p flex-column jc-center ai-center">
                    <div className="ratio ratio-21x9">
                      <TopicVideoTag block={block} />
                    </div>
                    <div className="___icon_label_button">
                      <>
                        <input
                          placeholder="Add caption here..."
                          style={{ width: '50%' }}
                          value={block.caption || ''}
                          onChange={(event) => {
                            const caption = event.target.value;
                            setBlocks(
                              blocks.map((x) => {
                                if (x.id === block.id) {
                                  x.caption = caption;
                                }

                                return x;
                              })
                            );
                          }}
                        />
                      </>
                      {canSave() && <BlockDeleteBtn block={block} />}
                      {canSave() && (
                        <AppButton
                          variantClass="btn btn-md btn-danger mx-4"
                          onClick={() => {
                            toggleUploadModal({ currentBlock: 'video', block });
                          }}
                        >
                          <BsPencil />
                          Edit Block
                        </AppButton>
                      )}
                    </div>
                  </div>
                )}
                {block.currentBlock === 'audio' && (
                  <div className="__audio_tool ___blockTools blue__light__border flex-p flex-column jc-center ai-center">
                    <div className="ratio ratio-21x9">
                      <TopicAudioTag block={block} />
                    </div>
                    <div className="___icon_label_button">
                      <>
                        <input
                          placeholder="Add caption here..."
                          style={{ width: '50%' }}
                          value={block.caption || ''}
                          onChange={(event) => {
                            const caption = event.target.value;
                            setBlocks(
                              blocks.map((x) => {
                                if (x.id === block.id) {
                                  x.caption = caption;
                                }

                                return x;
                              })
                            );
                          }}
                        />
                      </>
                      {canSave() && <BlockDeleteBtn block={block} />}
                      {canSave() && (
                        <AppButton
                          variantClass="btn btn-md btn-danger mx-4"
                          onClick={() => {
                            toggleUploadModal({ currentBlock: 'audio', block });
                          }}
                        >
                          <BsPencil />
                          Edit Block
                        </AppButton>
                      )}
                    </div>
                  </div>
                )}
                {block.currentBlock === 'file' && (
                  <div className="__file_tool ___blockTools blue__light__border flex-p flex-column jc-center ai-center">
                    <div className="card border-dark mb-3" style={{ width: '18rem' }}>
                      <a href={block.assetUrl} target="_blank" rel="noopener noreferrer">
                        <img src={AppPics.FileSvg} className="card-img-top" alt="" />
                        <p className="h6 text-center my-2">Click to open in a new tab</p>
                      </a>
                    </div>
                    <div className="___icon_label_button">
                      <>
                        <input
                          placeholder="Add caption here..."
                          style={{ width: '50%' }}
                          value={block.caption || ''}
                          onChange={(event) => {
                            const caption = event.target.value;
                            setBlocks(
                              blocks.map((x) => {
                                if (x.id === block.id) {
                                  x.caption = caption;
                                }

                                return x;
                              })
                            );
                          }}
                        />
                      </>
                      {canSave() && <BlockDeleteBtn block={block} />}
                      {canSave() && (
                        <AppButton
                          variantClass="btn btn-md btn-danger mx-4"
                          onClick={() => {
                            toggleUploadModal({ currentBlock: 'file', block });
                          }}
                        >
                          <BsPencil />
                          Edit Block
                        </AppButton>
                      )}
                    </div>
                  </div>
                )}
                {block.currentBlock === 'equation' && (
                  <div className="__equation_tool col-12">
                    <EquationBox
                      equation={DOMPurify.sanitize(block.value)}
                      showProcessButton={false}
                      setEquation={(value) => {
                        setBlocks(
                          blocks.map((x) => {
                            if (x.id === block.id) return { ...x, value };
                            return x;
                          })
                        );
                      }}
                    />
                    <div className="___icon_label_button">
                      {canSave() && <BlockDeleteBtn block={block} />}
                      <PreviewBtn block={block} />
                      {/* <AddBlock block={block} /> */}
                    </div>
                  </div>
                )}
              </Fragment>
            );
          })}
          {canSave() && (
            <div className="___placeBlockBuilder">
              <AppBlocks
                handleChange={(currentBlock) => {
                  if (['video', 'image', 'audio', 'file'].includes(currentBlock)) {
                    toggleUploadModal({ currentBlock });
                    return;
                  }
                  setBlocks(
                    blocks.concat({
                      ...blockMasterSchema,
                      id: getRandomUid(),
                      currentBlock,
                    })
                  );
                }}
              />
            </div>
          )}
        </div>

        <div className="row mt-4">
          {canChangeStatus() || isAdmin ? (
            <div className="col-6">
              <ReviewContent
                area={userPermissions?.topic?.geekCorner}
                value={status}
                onChange={(value) => setStatus(value)}
                selectProps={{ disabled: teacherCannotChangeStatus }}
              />
              {teacherCannotChangeStatus && (
                <small className="text-danger">
                  Please give a rating first and then change the assignee status.
                </small>
              )}
            </div>
          ) : (
            <div className="col-6">
              {status === 'draft' && <div>The section is on draft mode.</div>}
              {status === 'review' && <div>The section is pending with Subject expert </div>}
              {status === 'qc_review' && <div>The section is pending with QC review</div>}
              {status === 'teacher_review' && <div>The section is pending for Teacher review</div>}
            </div>
          )}
          <div className="col-6">
            <label>Content owned by</label>
            <UserSelect
              dependencies={{ query: { roles: [ROLES.SUBJECT_EXPERT] } }}
              handleChange={setContentOwner}
              values={contentOwner}
              isMulti={false}
            />
          </div>
          {((isSE && status === 'teacher_review') || isAdmin) && (
            <div className="col-6 mt-4">
              <label>Please pick a teacher</label>
              <UserSelect
                dependencies={{ query: { roles: [ROLES.TUTOR] } }}
                handleChange={setContentTutor}
                values={contentTutor}
                isMulti={false}
              />
            </div>
          )}
          {isTeacher && !rating && (
            <div className="col-6">
              <TutorRating ratingValue={rating} apiArgs={{ geekID: geek[0]?._id, type: 'geek' }} />
            </div>
          )}
        </div>

        <div className="row mt-5 btnsubmitmain __flex-j-c-center">
          {canSave('bypass') && (
            <div className="col-3 btnTab-foot">
              <button
                type="button"
                className="btn-right-content save"
                onClick={() => {
                  if (status === 'teacher_review' && !contentTutor) {
                    window.alert('Please pick a teacher');
                    return;
                  }
                  handleSave();
                }}
              >
                Save Changes
              </button>
            </div>
          )}
          <div className="col-3 btnTab-foot">
            <a
              href={`${process.env.REACT_APP_PREVIEW_URL.replace(/\/$/, '')}/sneak-peak?view=geek&course=${courseID}&chapter=${chapterID}&lesson=${topicID}&subtopic=${subtopicID}`}
              role={'button'}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-plane g--tint"
            >
              {' '}
              Live Preview{' '}
            </a>
          </div>
          {isTeacher && rating && (
            <div className="col-3 btnTab-foot">
              <button
                type="button"
                className="btn-plane p--tint m-l-btn"
                onClick={() => {
                  setUpdatingRatingModal({
                    show: true,
                    data: {
                      currentRating: rating,
                      apiArgs: {
                        geekID: geek[0]?._id,
                        type: 'geek',
                      },
                    },
                  });
                }}
              >
                Change rating
              </button>
            </div>
          )}
          <div>
            {(isAdmin || isSE) && reOrderBlocks?.length > 0 && (
              <RearrangeBlocks
                blocks={reOrderBlocks}
                type="GEEK"
                handleSubmission={(args = {}) => {
                  handleSave({}, args);
                }}
                checkPrerequisite={() => {
                  const cannotProceed = blocks?.some((b) => isValidUuid(b.id));

                  return { canGoForward: !cannotProceed };
                }}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default TopicGeekCornerTab;

const manageGeekSchema = Joi.object({
  status: Joi.string().required().trim(),
  blocks: Joi.array().required(),
});
